import { AttendanceType } from '@fingertip/creator-proto/gen/fingertip/common/enum/v1/attendance_type_pb'
import { EventType } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/event_type_pb'
import { Media } from '@fingertip/creator-proto/gen/fingertip/common/type/v1/media_pb'
import { useMemo } from 'react'

import { eventLocationToBookingLocationMapper } from '@/components/booking/mappers'
import { BookingLocation } from '@/lib/stores/booker-store'

export type BookingItemsData = {
  eventTypeId: string
  quantity: number
  eventType: EventType
}[]

export type BookingForm = {
  formTemplateId: string
  eventTypes: string[]
  summary: string
}

export type BookingSelection = {
  bookingItems: BookingItemsData
  bookingItemsColors: string[]
  allLocations: BookingLocation[]
  bookingItemsLocation: BookingLocation
  bookingItemsTitle: string
  bookingItemsDescription: string
  bookingItemsMedia?: Media
  bookingItemsPriceInCents: number
  bookingItemsLength: number
  bookingItemsAttendanceType: AttendanceType
  bookingItemsShowAttendeeCount: boolean
  eventTypes: {
    [key: string]: number
  }
  bookingForms: BookingForm[]
}

export const useBookingSelection = (bookingItems: BookingItemsData) => {
  return useMemo((): BookingSelection | undefined => {
    if (bookingItems.length === 0) {
      return undefined
    }

    // Location
    const allLocations = bookingItems
      .map((item) =>
        eventLocationToBookingLocationMapper(item.eventType.locations[0]),
      )
      .reduce((acc: BookingLocation[], location) => {
        const found = acc.find(
          (item) =>
            item.value === location.value && item.type === location.type,
        )
        if (!found) {
          return [...acc, location]
        } else {
          return acc
        }
      }, [])
    const bookingItemsLocation = allLocations.reduce((acc, location) => {
      const valueMap = {
        HOST_IN_PERSON: 6,
        ATTENDEE_IN_PERSON: 5,
        CONFERENCING: 4,
        ATTENDEE_PHONE: 3,
        HOST_PHONE: 2,
        LINK: 1,
        UNSPECIFIED: 0,
      }
      const accValue = valueMap[acc.type]
      const locationValue = valueMap[location.type]
      if (locationValue > accValue) {
        return location
      } else {
        return acc
      }
    }, allLocations[0])

    // Price and info
    const {
      title,
      description,
      media,
      priceInCents,
      length,
      attendanceType,
      colors,
    } = bookingItems.reduce(
      (acc, item, index, arr) => {
        if (arr.length === 1) {
          return {
            title: item.eventType.name,
            description: item.eventType.description,
            media: item.eventType.media,
            priceInCents: item.eventType.priceInCents * item.quantity,
            length: item.eventType.length * item.quantity,
            attendanceType: item.eventType.attendanceType,
            colors: [bookingItems[0].eventType.color],
          }
        } else {
          return {
            title: `${index + 1} event types`,
            description:
              index === 0
                ? `${item.quantity} x ${item.eventType.name}`
                : acc.description +
                  `, ${item.quantity} x ${item.eventType.name}`,
            media: item.eventType.media,
            priceInCents:
              acc.priceInCents + item.eventType.priceInCents * item.quantity,
            length: acc.length + item.eventType.length * item.quantity,
            attendanceType: item.eventType.attendanceType,
            colors: [...acc.colors, item.eventType.color],
          }
        }
      },
      {
        title: '',
        description: '',
        media: bookingItems[0].eventType.media,
        priceInCents: 0,
        length: 0,
        attendanceType: bookingItems[0].eventType.attendanceType,
        colors: [] as string[],
      },
    )

    const eventTypes = bookingItems.reduce((acc, item) => {
      return { ...acc, [item.eventTypeId]: item.quantity }
    }, {})

    const bookingForms: BookingForm[] = bookingItems.reduce(
      (acc: BookingForm[], item) => {
        const found = acc.find(
          (form) => form.formTemplateId === item.eventType.formTemplateId,
        )
        if (found) {
          found.eventTypes.push(item.eventTypeId)
          found.summary = `${found.summary}, ${item.eventType.name}`
        } else if (!!item.eventType.formTemplateId) {
          acc.push({
            formTemplateId: item.eventType.formTemplateId,
            eventTypes: [item.eventTypeId],
            summary: item.eventType.name,
          })
        }
        return acc
      },
      [],
    )

    return {
      bookingItems,
      bookingItemsColors: colors,
      allLocations,
      bookingItemsLocation,
      bookingItemsTitle: title,
      bookingItemsDescription: description,
      bookingItemsMedia: media,
      bookingItemsPriceInCents: priceInCents,
      bookingItemsLength: length,
      bookingItemsAttendanceType: attendanceType,
      bookingItemsShowAttendeeCount:
        bookingItems.length === 1
          ? bookingItems[0].eventType.showAttendeeCount
          : false,
      eventTypes,
      bookingForms,
    }
  }, [bookingItems])
}
